import {
  ActionIcon,
  CheckIcon,
  Combobox,
  Group,
  Input,
  InputBase,
  Loader,
  Text,
  useCombobox
} from '@mantine/core'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { QueryKey, useQuery } from '@tanstack/react-query'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { PaginatedResponse } from '@/types/pagination'

const getDropdownContent = (
  isLoading: boolean,
  isError: boolean,
  options: ReactNode[]
) => {
  if (isLoading) {
    return (
      <Combobox.Empty fz={13}>
        <FormattedMessage id="general.loading" />
      </Combobox.Empty>
    )
  }

  if (isError) {
    return (
      <Combobox.Empty fz={13}>
        <FormattedMessage id="general.list.error" />
      </Combobox.Empty>
    )
  }

  if (options.length === 0) {
    return (
      <Combobox.Empty fz={13}>
        <FormattedMessage id="general.list.empty" />
      </Combobox.Empty>
    )
  }

  return options
}

export type FilterSelectAsyncProps<T, R = T[] | PaginatedResponse<T[]>> = {
  value?: string | null
  placeholder?: string
  width?: number
  queryFn: () => Promise<R>
  queryKey: QueryKey
  selectFn?: (data: R) => T[]
  getOptionValue: (item: T) => string
  getOptionLabel: (item: T) => string
  onChange?: (value: string | null) => void
}

export const FilterSelectAsync = <T, R = T[] | PaginatedResponse<T[]>>({
  value,
  placeholder,
  width,
  queryFn,
  queryKey,
  selectFn,
  getOptionValue,
  getOptionLabel,
  onChange
}: FilterSelectAsyncProps<T, R>) => {
  const [isOpen, setIsOpen] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryFn,
    queryKey,
    select: selectFn,
    enabled: isOpen || !!value,
    retry: 1
  })

  const results = useMemo(() => data || [], [data])

  const combobox = useCombobox({
    onDropdownClose: () => {
      setIsOpen(false)
      combobox.resetSelectedOption()
    },
    onDropdownOpen: () => {
      setIsOpen(true)
    }
  })

  const getInputDisplayValue = useCallback(() => {
    if (value) {
      const item = results.find((item) => getOptionValue(item) === value)

      if (item) {
        return (
          <Text size="sm" truncate>
            {getOptionLabel(item)}
          </Text>
        )
      }
    }

    return <Input.Placeholder>{placeholder}</Input.Placeholder>
  }, [value, results, placeholder, getOptionLabel, getOptionValue])

  const options = useMemo(
    () =>
      results.map((item) => (
        <Combobox.Option
          key={getOptionValue(item)}
          value={getOptionValue(item)}
        >
          <Group gap="xs" wrap="nowrap">
            {getOptionValue(item) === value && <CheckIcon size={12} />}
            <span>{getOptionLabel(item)}</span>
          </Group>
        </Combobox.Option>
      )),
    [results, getOptionValue, getOptionLabel, value]
  )

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange?.(val)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <InputBase
          w={width}
          component="button"
          type="button"
          rightSection={
            isLoading ? (
              <Loader size={16} />
            ) : value ? (
              <ActionIcon
                variant="light"
                size={16}
                radius="xl"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onChange?.(null)}
              >
                <IconX style={{ width: '80%', height: '80%' }} stroke={2} />
              </ActionIcon>
            ) : (
              <IconChevronDown size={16} stroke={2} />
            )
          }
          rightSectionPointerEvents={value ? 'all' : 'none'}
          pointer
          onClick={() => combobox.toggleDropdown()}
        >
          {getInputDisplayValue()}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={320} style={{ overflowY: 'auto' }}>
          {getDropdownContent(isLoading, isError, options)}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
